/**
 * Wrapper mínimo sobre fetch para hablar con la API NestJS bajo /api.
 * En producción (mismo proceso Fastify sirviendo API + frontend) esto
 * resuelve a la misma URL; en desarrollo, Vite lo redirige al backend
 * (ver server.proxy en vite.config.ts).
 *
 * El access token vive en memoria (nunca en localStorage, para no exponerlo
 * a XSS) y lo administra lib/auth.tsx, que se registra aquí como
 * "refrescador" para reintentar una vez, en silencio, cuando expira
 * (usando el refresh token en cookie httpOnly — sección 4 del proyecto).
 */
import type { ZodType } from "zod";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly detalle?: unknown,
  ) {
    super(message);
  }
}

let accessTokenActual: string | null = null;
let refrescarSesion: (() => Promise<string | null>) | null = null;

export function establecerAccessToken(token: string | null): void {
  accessTokenActual = token;
}

export function registrarRefrescador(fn: (() => Promise<string | null>) | null): void {
  refrescarSesion = fn;
}

interface OpcionesApi<T = unknown> {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  /**
   * Si se pasa, la respuesta se valida contra este esquema Zod antes de
   * devolverla. Sin esto, un desajuste entre lo que el backend realmente
   * responde y el tipo TS que el hook espera falla en silencio (o revienta
   * más abajo, en un componente, con un error confuso) en vez de dar un
   * error claro aquí mismo.
   */
  schema?: ZodType<T>;
  /** Timeout personalizado en ms para peticiones largas (ej. pasarelas de pago externas). */
  timeoutMs?: number;
}

// Tiempo máximo de espera por respuesta del servidor (en ms).
// Sin esto, un fetch() puede quedarse colgado hasta 5 minutos si el
// servidor no responde (Wi-Fi inestable, MAMP pausado, etc.), acumulando
// requests "zombie" que al resolverse de golpe congelan la tablet.
const TIMEOUT_MS = 8000;

export async function apiFetch<T>(ruta: string, opciones: OpcionesApi<T> = {}, reintentado = false): Promise<T> {
  const { method = "GET", body, schema, timeoutMs = TIMEOUT_MS } = opciones;

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (accessTokenActual) {
    headers["Authorization"] = `Bearer ${accessTokenActual}`;
  }

  // AbortController para cancelar el fetch si tarda más del timeout asignado.
  // Esto evita la acumulación de peticiones "zombie" que congela la UI.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let respuesta: Response;
  try {
    respuesta = await fetch(`/api${ruta}`, {
      method,
      headers,
      body: typeof body === "string" ? body : body !== undefined ? JSON.stringify(body) : undefined,
      // Necesario para que la cookie httpOnly del refresh token viaje en /auth/*.
      credentials: "include",
      signal: controller.signal,
    });
  } catch (err: unknown) {
    clearTimeout(timeoutId);
    // Si fue un abort por timeout, lanzar error más descriptivo.
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError(`Tiempo de espera agotado al llamar ${ruta} (>${timeoutMs / 1000}s)`, 408);
    }
    throw err;
  }
  clearTimeout(timeoutId);

  if (respuesta.status === 401 && !reintentado && refrescarSesion && !ruta.startsWith("/auth/")) {
    const nuevoToken = await refrescarSesion();
    if (nuevoToken) {
      return apiFetch<T>(ruta, opciones, true);
    }
  }

  const contentType = respuesta.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? await respuesta.json() : undefined;

  if (!respuesta.ok) {
    const mensaje =
      (data && typeof data === "object" && "mensaje" in data && String(data.mensaje)) ||
      (data && typeof data === "object" && "message" in data && String(data.message)) ||
      `Error ${respuesta.status} al llamar ${ruta}`;
    throw new ApiError(mensaje, respuesta.status, data);
  }

  if (schema) {
    const resultado = schema.safeParse(data);
    if (!resultado.success) {
      // eslint-disable-next-line no-console
      console.error(`Respuesta de ${ruta} no coincide con el esquema esperado:`, resultado.error.issues, data);
      throw new ApiError(
        `La respuesta del servidor para ${ruta} no tiene el formato esperado.`,
        respuesta.status,
        resultado.error.issues,
      );
    }
    return resultado.data;
  }

  return data as T;
}
